import { PeriodLayout } from "./periodLayout";
import { MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useTenantTranslation } from "hooks/formatters";
import {
  AutoRecipeParameterDefinition,
  AutoRecipeParameters,
  AutoRecipeParameterScope,
  AutoRecipeParameterWidget,
} from "store/api/generatedApi";
import { RuleParameterEditor } from "./RuleParameterEditor";
import {
  ConditionalInventoryTargets,
  ConditionalMaterialLimits,
} from "./ruleParameters";

export const isConditionalWidget = (widget: AutoRecipeParameterWidget) =>
  widget === "conditional_inventory_targets" ||
  widget === "material_limit_overrides";

type ConditionalRule = ConditionalInventoryTargets | ConditionalMaterialLimits;

export const ConditionalRulesEditor = ({
  parameters,
  periodLayout,
  definition,
  scope,
  onChange,
  disabled,
}: {
  parameters: AutoRecipeParameters;
  periodLayout: PeriodLayout;
  definition: AutoRecipeParameterDefinition;
  scope: AutoRecipeParameterScope;
  onChange: (parameters: AutoRecipeParameters) => void;
  disabled: boolean;
}) => {
  const { t } = useTenantTranslation();
  const [selectedCondition, setSelectedCondition] = useState<string | null>(
    null
  );
  const fields = definition.sections.flatMap((section) =>
    section.fields
      .filter(
        (field) =>
          field.scopes.includes(scope) && isConditionalWidget(field.widget)
      )
      .flatMap((field) => {
        const values = parameters[section.name] as
          Record<string, unknown> | undefined;
        const rules: unknown = values?.[field.name];
        return Array.isArray(rules) && rules.length > 0
          ? [
              {
                section: section.name,
                field,
                values,
                rules: rules as ConditionalRule[],
              },
            ]
          : [];
      })
  );
  const conditions = [
    ...new Set(
      fields.flatMap(({ rules }) => rules.map((rule) => rule.condition))
    ),
  ];
  const condition =
    selectedCondition !== null && conditions.includes(selectedCondition)
      ? selectedCondition
      : conditions[0];
  if (!condition) return null;
  return (
    <Stack gap={2}>
      <Typography variant="h6">{t("autoConditionalRules")}</Typography>
      <TextField
        select
        label={t("autoCondition")}
        value={condition}
        helperText={t("autoConditionSelectionHelp")}
        onChange={(event) => {
          setSelectedCondition(event.target.value);
        }}
      >
        {conditions.map((condition) => (
          <MenuItem key={condition} value={condition}>
            {t(condition, { defaultValue: condition.replaceAll("_", " ") })}
          </MenuItem>
        ))}
      </TextField>
      {fields.map(({ section, field, values, rules }) => {
        const selectedRules = rules.filter(
          (rule) => rule.condition === condition
        );
        if (selectedRules.length === 0) return null;
        const limits = field.widget === "material_limit_overrides";
        return (
          <Stack key={`${section}/${field.name}/${condition}`} gap={1}>
            <Typography variant="subtitle1">
              {t(limits ? "materialLimitOverrides" : "targetInventory")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(
                limits
                  ? "autoConditionalLimitsHelp"
                  : "autoConditionalTargetsHelp"
              )}
            </Typography>
            <RuleParameterEditor
              widget={field.widget}
              value={selectedRules}
              disabled={disabled}
              periodLayout={periodLayout}
              onChange={(updated) => {
                const edited = updated as ConditionalRule[];
                onChange({
                  ...parameters,
                  [section]: {
                    ...values,
                    [field.name]: rules.map(
                      (rule) =>
                        edited.find(
                          (candidate) =>
                            candidate.rule_index === rule.rule_index
                        ) ?? rule
                    ),
                  },
                });
              }}
            />
          </Stack>
        );
      })}
    </Stack>
  );
};
